'use client'
import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'

const maxWidthNum = (maxWidth)=>{
  switch(maxWidth){
    case('sm'): return 240;
    case('lg'): return 760;
    case('xl'): return 960;
    default: return 480; // md
  } 
}

export const Dialog = ({
    open,
    onClose,
    children,
    title,
    className,
    maxWidth: propsMaxWidth = 'md',
    contentPaddingClassName = 'p-2',
    fullScreen = false,
    hideTitle = false,
    id,
  }: {
    open: boolean
    onClose?: () => void
    children: React.ReactNode
    title?: string | React.ReactNode
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl'
    contentPaddingClassName?: string
    fullScreen?: boolean
    hideTitle?: boolean
    id?: string
    className?: string
  }) => {

    const dialogRef = useRef<HTMLDialogElement>(null)

    function onClick(event) {
      // Read dialogRef.current fresh (not a value closed over at render
      // time) so this still works correctly for the very first render,
      // before the ref has attached.
      if (event.target === dialogRef.current) {
        dialogRef.current.close()
      }
    }

    useEffect(()=>{
      const dialog = dialogRef.current
      if(!dialog) return
      if(open){
        dialog.showModal?.()
        requestAnimationFrame(() => {
          dialog?.focus() // or focus some neutral element
        })
      } else {
        dialog.close?.()
      }
    }, [open])

    useEffect(() => {
      const dialog = dialogRef.current
      if (!dialog) return null
  
      const handleClose = () => {
        onClose?.()
        dialog.close?.()
      }
  
      dialog.addEventListener('close', handleClose)
      dialog.addEventListener('cancel', handleClose)
      dialog.addEventListener("click", onClick)
      
      return () => {
        dialog.removeEventListener('close', handleClose)
        dialog.removeEventListener('cancel', handleClose)
        dialog.removeEventListener("click", onClick)
      }
    }, [onClose])

    const width = fullScreen ? '100vw' : null
    const height = fullScreen ? '100vh' : null
    const maxWidth = width ? null : maxWidthNum(propsMaxWidth) 
    const style = { maxWidth, width, height }
    const outerClasses = fullScreen ? 'py-8 p-3' : 'py-8 p-3'
    const innerClasses = fullScreen ? 'h-full' : 'overflow-scroll max-h-full'
    return (
      <dialog id={id} className={`modal`} ref={dialogRef}>
        <div className={clsx('h-screen w-full px-0 flex items-center', outerClasses)} style={style}>
          <div className={clsx(
            'bg-base-100 flex flex-col flex-1 w-full rounded-xl shadow-lg',
            innerClasses, className
          )} style={style}>
            
            { !hideTitle && <div className='flex justify-between items-start w-full p-2 pl-4' style={{ width, maxWidth }}>
              <div className={clsx('text-xl')}>
                { title }
              </div>
              <button
                onClick={onClose}
                className="btn btn-sm btn-circle"
              >
                ✕
              </button>
            </div> }

            <div className={contentPaddingClassName}>
              { open && children }
            </div>

          </div>
        </div>
      </dialog>
    );
  }

export const DialogTitle = ({ children }: { children: React.ReactNode }) => {
    return <h3 className="text-lg font-bold">{children}</h3>;
}

export const DialogContent = ({ children, ...restProps }: { children: React.ReactNode, id?: string }) => {
    return <div className="py-4" {...restProps}>{children}</div>;
}
